<template>
  <div class="search-box-global" :class="{ 'search-box-global--dark': $theme.darkTheme }" v-click-outside="clearSearch">
    <div class="search-box-global__container" :class="{ 'search-box-global__container--dirty': isDirty }">
      <label class="search-box-global__field">
        <i class="material-icons">search</i>

        <input type="search" name="search-global" v-model="searchTerm" autocomplete="off" :style="{ '--input-focus-color': $theme.primaryColor }" />

        <SpinnerWave v-if="searchTerm && isLoading" class="search-box-global__loading" />
        <i v-else-if="searchTerm" class="material-icons search-box-global__clear" @click.prevent="clearSearch">close</i>
      </label>

      <div v-if="searchTerm" class="search-box-global__results">
        <ul>
          <li v-if="hasNoResults || isLoading" class="header-search-result autocomplete-no-results center-align">
            <template v-if="isLoading">Carregando...</template>
            <template v-else-if="hasNoResults"> Nenhum resultado foi encontrado </template>
          </li>
          <template v-for="(resultItem, index) in searchResults">
            <li :key="'opt_' + index" class="header-search-result">
              <GlobalSearchResultCourse v-if="type === 'courses'" :course="resultItem" @click="itemClick" />
              <template v-else-if="type === 'learningObjects'">
                <GlobalSearchResultModule
                  v-if="resultItem.resultType === 'module'"
                  :module="resultItem"
                  @click="itemClick"
                />
                <GlobalSearchResultLearningObjects
                  v-else-if="resultItem.resultType === 'learningObjects'"
                  :module="resultItem"
                  @click="itemClick"
                />
              </template>
            </li>
            <hr v-if="index < searchResults.length - 1" />
          </template>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import GlobalSearchResultCourse from "@/components/GlobalSearchBar/ResultCourse.vue";
import GlobalSearchResultLearningObjects from "@/components/GlobalSearchBar/ResultLearningObjects.vue";
import GlobalSearchResultModule from "@/components/GlobalSearchBar/ResultModule.vue";
import SpinnerWave from "@/components/SpinnerWave.vue";
import clickOutside from "@/directives/clickOutside";
import { clearStringAccents } from "@/filters/clearStringAccents";

function normalizeString(string) {
  if (!string?.trim()) return "";
  return clearStringAccents(string.trim().toLowerCase());
}

export default {
  name: "GlobalSearchBar",
  directives: {
    clickOutside,
  },
  components: {
    GlobalSearchResultLearningObjects,
    GlobalSearchResultModule,
    GlobalSearchResultCourse,
    SpinnerWave,
  },
  emits: ["click"],
  props: {
    items: Array,
    filterPropName: String,
    type: {
      type: String,
      default: "learningObjects",
      validator: (type) => ["courses", "learningObjects"].includes(type),
    },
  },
  data() {
    return {
      searchTerm: "",
      isLoading: false,
      isPending: false,
      searchTimeout: null,
      searchResults: [],
    };
  },
  computed: {
    isDirty() {
      return this.searchTerm.length > 0;
    },
    hasNoResults() {
      return !this.searchResults.length && this.searchTerm.length > 0 && !this.isPending;
    },
  },
  watch: {
    searchTerm(val) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.isPending = !!val.trim();
      this.searchTimeout = setTimeout(() => {
        this.isPending = false;
        this.search();
      }, 300);
    },
  },
  methods: {
    clearSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTerm = "";
      this.searchResults = [];
      this.isPending = false;
    },
    itemClick(item) {
      this.clearSearch();
      this.$emit("click", item);
    },
    search() {
      if (!this.searchTerm.trim()) {
        this.searchResults = [];
        this.isLoading = false;
        return;
      }

      this.isLoading = true;

      switch (this.type) {
        case "courses":
          this.filterCourses();
          break;
        case "learningObjects":
          this.filterLearningObjects();
          break;
      }

      this.isLoading = false;
    },
    filterCourses() {
      const filterPropName = this.filterPropName;
      const normalizedSearchTerm = normalizeString(this.searchTerm);

      this.searchResults = this.items
        .filter((course) => normalizeString(course[filterPropName]).includes(normalizedSearchTerm))
        .map((course) => ({ ...course, resultType: "course" }));
    },
    filterLearningObjects() {
      const filterPropName = this.filterPropName;
      const normalizedSearchTerm = normalizeString(this.searchTerm);

      this.searchResults = this.items.reduce((resultModules, module) => {
        // Match module
        if (normalizeString(module[filterPropName]).includes(normalizedSearchTerm))
          resultModules.push({
            ...module,
            learningObjects: [
              module.learningObjects.find((learningObject) => learningObject.progress < 100) ||
                module.learningObjects[0],
            ],
            resultType: "module",
          });

        // Match learning objects
        const learningObjectsMatch = module.learningObjects.filter((learningObject) =>
          normalizeString(learningObject[filterPropName]).includes(normalizedSearchTerm)
        );

        if (learningObjectsMatch.length === 0) return resultModules;

        resultModules.push({
          ...module,
          learningObjects: learningObjectsMatch,
          resultType: "learningObjects",
        });

        return resultModules;
      }, []);
    },
  },
};
</script>

<style lang="scss">
.search-box-global {
  position: relative;
  z-index: 99;

  &,
  .search-box-global__field {
    color: #121212;
  }

  &.search-box-global--dark {
    &,
    .search-box-global__field {
      color: #e7e7e7;
    }

    .search-box-global__container {
      &:hover,
      &:focus-within,
      &.search-box-global__container--dirty {
        .search-box-global__field {
          background: #363636;
          transition: border-radius 0.1s ease, background 0.15s ease, box-shadow 0.15s ease;
        }
      }
    }

    .search-box-global__results ul,
    .header-search-result {
      background: #131318;
      color: #efefef;
    }

    .clickable:hover {
      background-color: #353542;
    }
  }

  .row {
    margin-bottom: unset !important;
  }

  .row,
  .col {
    float: unset !important;
  }

  .row:after {
    content: unset !important;
  }

  hr {
    margin: 0.25rem 0;
    opacity: 0.5;
  }

  .search-box-global__container {
    width: 45px;
    position: relative;
    transition: width 0.3s ease;

    &:hover,
    &:focus-within,
    &.search-box-global__container--dirty {
      width: 400px;

      .search-box-global__field {
        justify-content: start;
        background: #dcdbdc;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
        transition: border-radius 0.1s ease, background 0.15s ease, box-shadow 0.15s ease;
      }
    }

    &.search-box-global__container--dirty .search-box-global__field {
      border-radius: 10px 10px 0 0;
    }
  }

  .search-box-global__field {
    width: 100%;
    height: 45px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    transition: border-radius 0.1s ease, background 0.4s ease, box-shadow 0.4s ease;

    input {
      margin-bottom: 0 !important;
      margin-right: 8px !important;
      color: currentColor;
      border: none !important;
      box-shadow: none !important;

      &:focus {
        border-bottom: 1px solid var(--input-focus-color) !important;
        box-shadow: 0 1px 0 var(--input-focus-color) !important;
      }

      &::-webkit-search-cancel-button {
        display: none;
      }
    }

    i {
      padding-inline: 0.725rem;
      user-select: none;
    }

    .search-box-global__loading {
      position: absolute;
      height: 1rem;
      width: 1rem;
      top: 1rem;
      right: 1rem;
    }

    .search-box-global__clear {
      padding-inline: 0.725rem;
      cursor: pointer;
      user-select: none;
      font-size: 20px;
      opacity: 0.6;
      transition: opacity 0.2s ease;
      flex-shrink: 0;

      &:hover {
        opacity: 1;
      }
    }
  }

  .search-box-global__results ul {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    padding-block: 1rem;

    max-height: 300px;
    overflow: auto;
    border-radius: 0 0 10px 10px;
    background: #efefef;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
  }

  .header-search-result {
    padding: 0 0.725rem;
    background: #efefef;
    color: #333333;
  }

  .clickable {
    cursor: pointer;
    padding: 0.725rem;
    transition: background-color 0.2s ease;
    border-radius: 8px;
  }

  .clickable:hover {
    background-color: #e0e0e0ff;
  }

  .global-result__item-label {
    font-family: robotomedium, sans-serif;
    font-size: 12px;
    line-height: 20px;
    font-weight: 600;
    opacity: 0.7;
    display: flex;
    align-items: center;
    gap: 6px;

    &::before {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
      flex-shrink: 0;
    }

    &--course::before { background: #0564b1; }
    &--module::before { background: #7C4DFF; }
    &--lo::before     { background: #00897B; }
  }

  .global-result__item-title {
    font-size: 14px;
    line-height: 18px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .global-result__type-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    flex-shrink: 0;

    &--course { background: rgba(5, 100, 177, 0.12); color: #0564b1; }
    &--module { background: rgba(124, 77, 255, 0.12); color: #7C4DFF; }
    &--lo     { background: rgba(0, 137, 123, 0.12);  color: #00897B; }
  }

  .global-result__type-icon {
    font-size: 18px;
    flex-shrink: 0;
  }

  .autocomplete-object-img {
    min-width: 57px;
  }
}

@media screen and (max-width: 768px) {
  .search-box-global__container {
    width: unset !important;
  }
  .search-box-global {
    transition: all 300ms ease;

    position: absolute;
    right: 100px;

    max-width: 45px;
    width: 100vw;
  }

  .search-box-global:focus-within,
  .search-box-global:has(.search-box-global__container--dirty) {
    right: 0;
    max-width: calc(100vw - 40px);
    padding-inline: 20px;
  }
}
</style>
